import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, FileText, TrendingUp, MapPin, Clock, Calendar, ArrowRight } from 'lucide-react';
import api from '../utils/api';
import Modal from './Modal';

const PlaceBidModal = ({ load, isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remainingBids, setRemainingBids] = useState(null);
  const [bidType, setBidType] = useState('yes');
  // Legacy fields kept for backwards compatibility
  const [altPickupDate, setAltPickupDate] = useState('');
  const [altPickupTime, setAltPickupTime] = useState('');
  const [altDeliveryDate, setAltDeliveryDate] = useState('');
  const [altDeliveryTime, setAltDeliveryTime] = useState('');
  // New state for all stops alternative dates/times
  const [altStops, setAltStops] = useState({});

  useEffect(() => {
    const checkRemainingBids = async () => {
      try {
        const response = await api.get(`/api/bids/remaining/${load.id}`);
        setRemainingBids(response.data.remaining_bids);
      } catch (error) {
        console.error('Error checking remaining bids:', error);
      }
    };

    if (isOpen) {
      checkRemainingBids();
      // Initialize altStops with current stop data when modal opens
      if (load.stops && load.stops.length > 0) {
        const initialAltStops = {};
        load.stops.forEach(stop => {
          // Extract date from ISO format (e.g., "2024-01-15T00:00:00" -> "2024-01-15")
          const dateStr = stop.date ? stop.date.split('T')[0] : '';
          // Time is already in HH:MM or HH:MM:SS format
          const timeStr = stop.time ? stop.time.substring(0, 5) : '';
          initialAltStops[stop.id] = {
            date: dateStr,
            time: timeStr
          };
        });
        setAltStops(initialAltStops);
      }
    }
  }, [isOpen, load.id, load.stops]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const bidData = {
        loadId: load.id,
        amount: parseFloat(amount),
        notes,
        bidType
      };

      if (bidType === 'alternative') {
        // Validate that all stops have dates
        const sortedStops = [...(load.stops || [])].sort((a, b) => a.sequence - b.sequence);
        const missingDates = sortedStops.filter(stop => !altStops[stop.id]?.date);
        
        if (missingDates.length > 0) {
          setError('All stops must have alternative dates specified');
          setIsSubmitting(false);
          return;
        }
        
        // Validate chronological order of stops
        for (let i = 1; i < sortedStops.length; i++) {
          const prevStop = sortedStops[i - 1];
          const currStop = sortedStops[i];
          
          const prevData = altStops[prevStop.id];
          const currData = altStops[currStop.id];
          
          if (prevData?.date && currData?.date) {
            // Combine date and time for comparison
            const prevDateTime = new Date(`${prevData.date}T${prevData.time || '00:00'}`);
            const currDateTime = new Date(`${currData.date}T${currData.time || '00:00'}`);
            
            if (currDateTime < prevDateTime) {
              const prevLabel = (prevStop.stop_type || prevStop.stopType) === 'pickup' ? 'Pickup' : 'Delivery';
              const currLabel = (currStop.stop_type || currStop.stopType) === 'pickup' ? 'Pickup' : 'Delivery';
              setError(
                `Stop ${i + 1} (${currLabel} at ${currStop.city}) cannot be scheduled before ` +
                `Stop ${i} (${prevLabel} at ${prevStop.city}). Please ensure stops are in chronological order.`
              );
              setIsSubmitting(false);
              return;
            }
          }
        }
        
        // Build altStops array for backend (include stop info for display purposes)
        // Handle both snake_case and camelCase for stop_type
        const altStopsData = sortedStops.map(stop => ({
          stop_id: stop.id,
          date: altStops[stop.id]?.date || '',
          time: altStops[stop.id]?.time ? altStops[stop.id].time + ':00' : null,
          stop_type: stop.stop_type || stop.stopType,
          city: stop.city,
          state: stop.state,
          sequence: stop.sequence
        }));
        
        bidData.altStops = altStopsData;
        
        // Also set legacy fields from first pickup and last delivery for backwards compatibility
        const firstPickup = sortedStops.find(s => (s.stop_type || s.stopType) === 'pickup');
        const lastDelivery = [...sortedStops].reverse().find(s => (s.stop_type || s.stopType) === 'delivery');
        
        if (firstPickup && altStops[firstPickup.id]) {
          bidData.altPickupDate = altStops[firstPickup.id].date;
          if (altStops[firstPickup.id].time) {
            bidData.altPickupTime = altStops[firstPickup.id].time + ':00';
          }
        }
        
        if (lastDelivery && altStops[lastDelivery.id]) {
          bidData.altDeliveryDate = altStops[lastDelivery.id].date;
          if (altStops[lastDelivery.id].time) {
            bidData.altDeliveryTime = altStops[lastDelivery.id].time + ':00';
          }
        }
      }

      await api.post('/api/bids', bidData);

      // The WebSocket will handle updating the Redux store
      onClose();
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred while placing the bid.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Place a Bid">
      <div>
        {remainingBids !== null && (
          <div className={`mb-4 p-3 rounded-md ${
            remainingBids === 0 ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
          }`}>
            <p className="text-sm font-medium">
              {remainingBids === 0 
                ? "You have reached the maximum number of bids for this load"
                : `You have ${remainingBids} bid${remainingBids !== 1 ? 's' : ''} remaining for this load`
              }
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bid Type
            </label>
            <div className="flex space-x-4">
              <div 
                className={`flex-1 p-3 border rounded-md cursor-pointer transition-all ${
                  bidType === 'yes' 
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => setBidType('yes')}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    checked={bidType === 'yes'}
                    onChange={() => setBidType('yes')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 font-medium">Yes Bid</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  I can accommodate all pickup and delivery times as specified
                </p>
              </div>
              
              <div 
                className={`flex-1 p-3 border rounded-md cursor-pointer transition-all ${
                  bidType === 'alternative' 
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => setBidType('alternative')}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    checked={bidType === 'alternative'}
                    onChange={() => setBidType('alternative')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 font-medium">Alternative Bid</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  I need to propose alternative pickup/delivery times
                </p>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {bidType === 'alternative' && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="p-4 bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl border border-gray-200 shadow-inner">
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 bg-indigo-100 rounded-lg">
                      <Calendar className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Propose Alternative Schedule</h3>
                      <p className="text-xs text-gray-500">Adjust dates and times for each stop</p>
                    </div>
                  </div>
                  
                  {/* Stops Timeline */}
                  <div className="max-h-72 overflow-y-auto pr-1 space-y-0">
                    {[...(load.stops || [])].sort((a, b) => a.sequence - b.sequence).map((stop, index, arr) => {
                      // Handle both snake_case and camelCase for stop_type
                      const stopType = stop.stop_type || stop.stopType;
                      const isPickup = stopType === 'pickup';
                      const isLast = index === arr.length - 1;
                      const hasChanged = altStops[stop.id]?.date !== stop.date?.split('T')[0] || 
                                        altStops[stop.id]?.time !== stop.time?.substring(0, 5);
                      
                      return (
                        <motion.div 
                          key={stop.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="relative"
                        >
                          {/* Timeline connector */}
                          <div className="absolute left-3 top-8 bottom-0 flex flex-col items-center">
                            {!isLast && (
                              <div className={`w-0.5 flex-grow ${isPickup ? 'bg-blue-200' : 'bg-emerald-200'}`} />
                            )}
                          </div>
                          
                          <div className={`relative pl-9 pb-4 ${isLast ? 'pb-0' : ''}`}>
                            {/* Timeline dot */}
                            <div className={`absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center shadow-sm ${
                              isPickup 
                                ? 'bg-gradient-to-br from-blue-400 to-blue-600' 
                                : 'bg-gradient-to-br from-emerald-400 to-emerald-600'
                            }`}>
                              <MapPin className="w-3.5 h-3.5 text-white" />
                            </div>
                            
                            {/* Stop Card */}
                            <div className={`bg-white rounded-lg border transition-all duration-200 ${
                              hasChanged 
                                ? 'border-indigo-300 ring-2 ring-indigo-100' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}>
                              {/* Stop Header */}
                              <div className={`px-3 py-2 border-b ${
                                isPickup ? 'bg-blue-50/50 border-blue-100' : 'bg-emerald-50/50 border-emerald-100'
                              } rounded-t-lg`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
                                      isPickup 
                                        ? 'bg-blue-100 text-blue-700' 
                                        : 'bg-emerald-100 text-emerald-700'
                                    }`}>
                                      {isPickup ? 'Pickup' : 'Delivery'}
                                    </span>
                                    <span className="text-sm font-medium text-gray-700">
                                      {stop.city}, {stop.state}
                                    </span>
                                  </div>
                                  {hasChanged && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                                      Modified
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Original Schedule */}
                              <div className="px-3 py-2 bg-gray-50/50 border-b border-gray-100">
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <Clock className="w-3 h-3" />
                                  <span>Original:</span>
                                  <span className="font-medium text-gray-600">
                                    {stop.date?.split('T')[0]} at {stop.time?.substring(0, 5)}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Alternative Schedule Inputs */}
                              <div className="p-3">
                                <div className="flex items-center gap-1 mb-2 text-xs font-medium text-indigo-600">
                                  <ArrowRight className="w-3 h-3" />
                                  <span>Your proposed schedule:</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label htmlFor={`altDate-${stop.id}`} className="sr-only">
                                      Alternative Date
                                    </label>
                                    <div className="relative">
                                      <input
                                        type="date"
                                        id={`altDate-${stop.id}`}
                                        value={altStops[stop.id]?.date || ''}
                                        onChange={(e) => setAltStops(prev => ({
                                          ...prev,
                                          [stop.id]: { ...prev[stop.id], date: e.target.value }
                                        }))}
                                        className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                                        required
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label htmlFor={`altTime-${stop.id}`} className="sr-only">
                                      Alternative Time
                                    </label>
                                    <div className="relative">
                                      <input
                                        type="time"
                                        id={`altTime-${stop.id}`}
                                        value={altStops[stop.id]?.time || ''}
                                        onChange={(e) => setAltStops(prev => ({
                                          ...prev,
                                          [stop.id]: { ...prev[stop.id], time: e.target.value }
                                        }))}
                                        className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
              Bid Amount ($)
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                placeholder="0.00"
                required
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm" id="price-currency">
                  USD
                </span>
              </div>
            </div>
          </div>
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 pt-3 pointer-events-none">
                <FileText className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                rows="3"
                placeholder="Any additional information..."
              ></textarea>
            </div>
          </div>
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-500 text-sm"
            >
              {error}
            </motion.p>
          )}
          <motion.button
            type="submit"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: remainingBids > 0 ? 1.05 : 1 }}
            whileTap={{ scale: remainingBids > 0 ? 0.95 : 1 }}
            disabled={isSubmitting || remainingBids === 0}
          >
            {isSubmitting ? (
              <motion.div
                className="h-5 w-5 border-t-2 border-b-2 border-white rounded-full animate-spin"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            ) : (
              <>
                <TrendingUp className="mr-2 h-5 w-5" aria-hidden="true" />
                Place Bid
              </>
            )}
          </motion.button>
        </form>
      </div>
    </Modal>
  );
};

export default PlaceBidModal;
